import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from '@ecs-environment';
import { PRODUCT_PICTURE_FALLBACK } from '@ecs-features/product-page/components/product-header/product-header.component';
import { isGroupWithDriveGroupAndSpareParts } from '@ecs-features/result/spare-parts-result/spare-parts.helper';
import {
    SparePart,
    SparePartGroupList,
    SpareParts,
    SparePartsResponseError,
} from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { NotificationService } from '@ecs-services/notification.service';
import { PackageService } from '@ecs-services/package.service';
import { PictureService } from '@ecs-services/picture/picture.service';
import { ProductLifeCycleStatusService } from '@ecs-services/product-life-cycle-status.service';
import { ProductReconstructionService } from '@ecs-services/product-reconstruction.service';
import { SearchStoreService } from '@ecs-services/search/search-store.service';
import { isSerialNumberResult, isSparePartListResult } from '@ecs-services/search/search.helper';
import {
    DriveGroupsWithSparePartsWear,
    PlantsWithSpareParts,
    SparePartService,
} from '@ecs-services/spare-part/spare-part.service';
import { searchTermChanged } from '@ecs-stores/store.helper';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService, PackageInformation, WINDOW } from '@os-components';
import { EMPTY, iif, of } from 'rxjs';
import { catchError, debounceTime, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
    SearchResultType,
    SerialNumberSearchResult,
    SparePartListSearchResult,
} from '../../../../services/search/search.interface';
import * as SearchActions from '../../search.actions';
import { DataStage } from '../../search.reducer';
import { selectPreviousSearchResult } from '../../search.selectors';
import * as ProductPageActions from './product-page.actions';

@Injectable()
export class ProductPageEffects {
    public requestImageUrlForSerialNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isSerialNumberResult),
            debounceTime(50),
            filter(({ pictureUrl }) => pictureUrl === undefined),
            switchMap(({ serialNumber }) =>
                this.pictureService.fetchStaticPictureURLForReplacementProduct(serialNumber).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of(PRODUCT_PICTURE_FALLBACK))
                )
            ),
            map((pictureUrl) => ProductPageActions.receiveProductPicture({ payload: { pictureUrl } }))
        )
    );

    public requestPackageInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isSerialNumberResult),
            concatLatestFrom(() => this.store.select(selectPreviousSearchResult)),
            // prevent duplicate notifications due to language change
            switchMap(([searchResult, previousSearchResult]) => {
                if (
                    !isSerialNumberResult(previousSearchResult) ||
                    searchResult.serialNumber !== previousSearchResult.serialNumber
                ) {
                    return this.packageService.getPackageInformation(searchResult.serialNumber).pipe(
                        takeUntil(searchTermChanged(this.searchStore)),
                        catchError(() => of(null))
                    );
                }

                return of(undefined);
            }),
            map((packageInformation) => {
                if (packageInformation === undefined) {
                    return SearchActions.noOpAction();
                } else {
                    return ProductPageActions.receiveProductPackageInformation({
                        payload: { packageInformation },
                    });
                }
            })
        )
    );

    public displayPackageInformation$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProductPageActions.receiveProductPackageInformation),
                map(({ payload: { packageInformation } }) => packageInformation),
                filter(
                    (packageInformation): packageInformation is PackageInformation =>
                        packageInformation !== null
                ),
                map((packageInformation) => {
                    this.notificationService.addInfo({
                        headline: 'PRODUCT.NOTIFICATION.PACKAGE.HEADING',
                        content: '',
                        links: [
                            {
                                href: `${this.window.location.href.split('?')[0]}?SEARCH_STRING=${
                                    packageInformation.main.id
                                }`,
                                // handle language change without removing & adding again
                                text: this.translateService.onLangChange.pipe(
                                    startWith(undefined),
                                    map(
                                        /* istanbul ignore next */
                                        () =>
                                            `${this.translateService.instant(
                                                `ID_TYPE.${packageInformation.main.type}`
                                            )} ${packageInformation.main.id} - ${
                                                packageInformation.main.name
                                            } (${this.translateService.instant(
                                                'PRODUCT.NOTIFICATION.PACKAGE.MAIN_ITEM'
                                            )})`
                                    )
                                ),
                            },
                            ...packageInformation.sub.map((item) => ({
                                href: `${this.window.location.href.split('?')[0]}?SEARCH_STRING=${item.id}`,
                                // handle language change without removing & adding again
                                text: this.translateService.onLangChange.pipe(
                                    startWith(undefined),
                                    map(
                                        /* istanbul ignore next */
                                        () =>
                                            `${this.translateService.instant(`ID_TYPE.${item.type}`)} ${
                                                item.id
                                            } - ${item.name} (${this.translateService.instant(
                                                'PRODUCT.NOTIFICATION.PACKAGE.SUB_ITEM'
                                            )})`
                                    )
                                ),
                            })),
                        ],
                    });
                })
            ),
        { dispatch: false }
    );

    public requestSparePartListWithNewVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isSparePartListResult),
            tap((searchResult) => {
                if (searchResult.updated) {
                    this.notificationService.addInfo({
                        headline: 'PRODUCT.NOTIFICATION.SPARE_PART_LIST.VERSION.INFO.HEADING',
                        content: {
                            key: 'PRODUCT.NOTIFICATION.SPARE_PART_LIST.VERSION.INFO.CONTENT',
                            params: {
                                oldId: searchResult.oldId,
                            },
                        },
                    });
                }
            })
        )
    );

    public requestSparePartsForSerialNumberOrSparePartList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map(({ payload: { searchResult } }) => searchResult),
            filter(
                (searchResult): searchResult is SparePartListSearchResult | SerialNumberSearchResult =>
                    isSerialNumberResult(searchResult) || isSparePartListResult(searchResult)
            ),
            filter(({ spareParts }) => spareParts === undefined),
            switchMap((searchResult) =>
                iif(
                    () => isSerialNumberResult(searchResult),
                    this.sparePartService.getSparePartsForSerialNumber(
                        (searchResult as SerialNumberSearchResult).serialNumber
                    ),
                    this.sparePartService.getSparePartsForSparePartList(
                        (searchResult as SparePartListSearchResult).id
                    )
                ).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    map((response) => ({
                        ...response,
                        groups: response.groups.map((group) => ({
                            ...group,
                            spareParts: group.spareParts
                                ? group.spareParts.map((sparePart) => ({
                                      ...sparePart,
                                      name: this.getNameWithAddedHints(sparePart),
                                  }))
                                : undefined,
                        })),
                    })),
                    catchError(({ error, statusText, status }: HttpErrorResponse) =>
                        of({ error, statusText, status })
                    )
                )
            ),
            map((response: SparePartGroupList | SparePartsResponseError) =>
                this.isSparePartsError(response)
                    ? ProductPageActions.receiveSparePartsError({
                          payload: { sparePartsError: response },
                      })
                    : ProductPageActions.receiveSpareParts({ payload: { spareParts: response } })
            )
        )
    );

    public requestAvailabilityForSpareParts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductPageActions.receiveSpareParts),
            map(({ payload: { spareParts } }) => spareParts),
            filter((spareParts) => spareParts !== undefined),
            map((sparePartsGroups) =>
                sparePartsGroups.groups.reduce(
                    (spareParts, group) =>
                        group.spareParts ? [...spareParts, ...group.spareParts] : [...spareParts],
                    [] as SpareParts
                )
            ),
            map((spareParts) =>
                spareParts.filter(
                    (sparePart) => sparePart.availabilityCode === '00' || sparePart.availabilityCode === '04'
                )
            ),
            switchMap((spareParts) => {
                const plantsWithSparePartsHelper = spareParts.reduce(
                    (plantsWithSpareParts: PlantsWithSpareParts, sparePart) => {
                        const existing = plantsWithSpareParts.find(
                            (plantWithSparePart) => plantWithSparePart.plantId === sparePart.plantId
                        );
                        if (existing) {
                            existing.spareParts = [
                                ...existing.spareParts,
                                { id: sparePart.id, availabilityCode: sparePart.availabilityCode },
                            ];
                        } else {
                            plantsWithSpareParts = [
                                ...plantsWithSpareParts,
                                {
                                    plantId: sparePart.plantId,
                                    spareParts: [
                                        { id: sparePart.id, availabilityCode: sparePart.availabilityCode },
                                    ],
                                },
                            ];
                        }

                        return plantsWithSpareParts;
                    },
                    []
                );

                return this.sparePartService
                    .getSparePartsAvailability(plantsWithSparePartsHelper)
                    .pipe(takeUntil(searchTermChanged(this.searchStore)));
            }),
            map((sparePartsWithAvailability) =>
                ProductPageActions.receiveSparePartsAvailability({
                    payload: { sparePartsWithAvailability },
                })
            )
        )
    );

    public requestWearForSpareParts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductPageActions.receiveSpareParts),
            map(({ payload: { spareParts } }) => spareParts),
            filter((groupList) => groupList !== undefined),
            map((groupList) => groupList.groups),
            map((groups) => groups.filter(isGroupWithDriveGroupAndSpareParts)),
            map((groups) =>
                groups.map((group) => ({
                    driveGroup: group.driveGroup,
                    sparePartIds: group.spareParts.map((sparePart) => sparePart.id),
                }))
            ),
            switchMap((requestBody) =>
                this.sparePartService.getSparePartsWear(requestBody).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    // #409863 Error handling does not show any error msg at the moment
                    catchError(() => EMPTY)
                )
            ),
            map((sparePartsWithWear) =>
                sparePartsWithWear.reduce((result: DriveGroupsWithSparePartsWear, group) => {
                    result[group.driveGroup] = group.spareParts;

                    return result;
                }, {})
            ),
            map((sparePartsWithWear) =>
                ProductPageActions.receiveSparePartsWear({
                    payload: { sparePartsWithWear },
                })
            )
        )
    );

    public keepImageUrlOnLanguageChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            filter(({ userRequested }) => !userRequested),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isSerialNumberResult),
            filter(({ pictureUrl }) => pictureUrl === undefined),
            concatLatestFrom(() => this.store.select(selectPreviousSearchResult)),
            map(([searchResult, previousSearchResult]) => {
                if (
                    !isSerialNumberResult(previousSearchResult) ||
                    searchResult.serialNumber !== previousSearchResult.serialNumber ||
                    previousSearchResult.pictureUrl === undefined
                ) {
                    return SearchActions.noOpAction();
                }

                return SearchActions.receiveSearchResult({
                    payload: {
                        searchResult: { ...searchResult, pictureUrl: previousSearchResult.pictureUrl },
                        searchError: null,
                    },
                    userRequested: false,
                });
            })
        )
    );

    public updateDataStage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            map((action) => {
                switch (action.payload.searchResult?.type) {
                    case SearchResultType.SERIALNUMBER:
                        return SearchActions.updateStage({ dataStage: DataStage.SUFFICIENT });
                    default:
                        return SearchActions.noOpAction();
                }
            })
        )
    );

    public requestProductLifeCycleStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            filter(({ userRequested }) => userRequested),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isSerialNumberResult),
            switchMap(({ serialNumber }) =>
                this.productLifeCycleStatusService.getStatus(serialNumber).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of({})),
                    filter(
                        (result): result is { productLifeCycleStatus: string } =>
                            (result as any).productLifeCycleStatus &&
                            typeof (result as any).productLifeCycleStatus === 'string'
                    ),
                    map((result) => result.productLifeCycleStatus)
                )
            ),
            map((productLifeCycleStatus) =>
                ProductPageActions.receiveProductLifeCycleStatus({ payload: { productLifeCycleStatus } })
            )
        )
    );

    public receiveProductLifeCycleStatus$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProductPageActions.receiveProductLifeCycleStatus),
                map(({ payload: { productLifeCycleStatus } }) => {
                    switch (productLifeCycleStatus) {
                        case 'RED':
                            this.notificationService.addError({
                                headline: 'PRODUCT.NOTIFICATION.PRODUCT_LIFE_CYCLE.ERROR.HEADING',
                                content: 'PRODUCT.NOTIFICATION.PRODUCT_LIFE_CYCLE.ERROR.CONTENT',
                                links: [
                                    {
                                        href: `${environment.baseURLs.api.domain}/os/efficiency/ConversionAid`,
                                        text: 'PRODUCT.NOTIFICATION.PRODUCT_LIFE_CYCLE.ERROR.LINK_TEXT',
                                    },
                                ],
                            });
                            break;
                        case 'YEL':
                            this.notificationService.addWarning({
                                headline: 'PRODUCT.NOTIFICATION.PRODUCT_LIFE_CYCLE.WARNING.HEADING',
                                content: 'PRODUCT.NOTIFICATION.PRODUCT_LIFE_CYCLE.WARNING.CONTENT',
                                links: [
                                    {
                                        href: `${environment.baseURLs.api.domain}/os/efficiency/ConversionAid`,
                                        text: 'PRODUCT.NOTIFICATION.PRODUCT_LIFE_CYCLE.WARNING.LINK_TEXT',
                                    },
                                ],
                            });
                            break;
                    }
                })
            ),
        { dispatch: false }
    );

    public requestProductReconstructionStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            filter(({ userRequested }) => userRequested),
            map(({ payload: { searchResult } }) => searchResult),
            filter(isSerialNumberResult),
            switchMap(({ serialNumber }) =>
                this.productReconstructionService
                    .getReconstructionStatus(serialNumber)
                    .pipe(takeUntil(searchTermChanged(this.searchStore)))
            ),
            map((reconstruction) =>
                ProductPageActions.receiveProductReconstructionStatus({ payload: { reconstruction } })
            )
        )
    );

    public receiveProductReconstructionStatus$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProductPageActions.receiveProductReconstructionStatus),
                tap(({ payload: { reconstruction } }) => {
                    // TODO check if api returns true and no newSerialnumber or old Serialnumbers (otherwise refactor!)
                    if (reconstruction.isReconstruction && reconstruction.newSerialNumber) {
                        this.notificationService.addWarning({
                            headline: 'PRODUCT.NOTIFICATION.PRODUCT_RECONSTRUCTION.WARNING.HEADING',
                            content: {
                                key: 'PRODUCT.NOTIFICATION.PRODUCT_RECONSTRUCTION.WARNING.CONTENT.NEW_SERIALNUMBER',
                                params: {
                                    serialnumber: reconstruction.newSerialNumber,
                                },
                            },
                            links: [
                                {
                                    href: `${this.window.location.href.split('?')[0]}?SEARCH_STRING=${
                                        reconstruction.newSerialNumber
                                    }`,
                                    text: {
                                        key: 'PRODUCT.NOTIFICATION.PRODUCT_RECONSTRUCTION.WARNING.LINK_TEXT.NEW_SERIALNUMBER',
                                        params: {
                                            serialnumber: reconstruction.newSerialNumber,
                                        },
                                    },
                                },
                            ],
                        });
                    } else if (reconstruction.isReconstruction) {
                        this.notificationService.addWarning({
                            headline: 'PRODUCT.NOTIFICATION.PRODUCT_RECONSTRUCTION.WARNING.HEADING',
                            content:
                                'PRODUCT.NOTIFICATION.PRODUCT_RECONSTRUCTION.WARNING.CONTENT.NO_SERIALNUMBER',
                            links: [
                                {
                                    href: `${
                                        this.window.location.href.split('?')[0]
                                    }/${this.languageService.language.toLowerCase()}/technical_pages/forwarding/forwarding_standorte.html`,
                                    text: 'PRODUCT.NOTIFICATION.PRODUCT_RECONSTRUCTION.WARNING.LINK_TEXT.FIND_CONTACT',
                                },
                            ],
                        });
                    }
                })
            ),
        { dispatch: false }
    );

    public requestRecommendations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductPageActions.receiveSpareParts),
            map(({ payload: { spareParts } }) => spareParts),
            // don't show parts recommendation for spare part list result
            filter((spareParts) => spareParts.groups.every((group) => group.driveGroup !== undefined)),
            filter((spareParts) => spareParts !== undefined),
            switchMap(({ groups }) =>
                this.sparePartService
                    .getSparePartsRecommendations(groups)
                    .pipe(takeUntil(searchTermChanged(this.searchStore)))
            ),
            map((recommendations) =>
                ProductPageActions.receiveSparePartRecommendations({
                    payload: { recommendations },
                })
            )
        )
    );

    public constructor(
        private actions$: Actions,
        private pictureService: PictureService,
        private sparePartService: SparePartService,
        private store: Store,
        private productLifeCycleStatusService: ProductLifeCycleStatusService,
        private productReconstructionService: ProductReconstructionService,
        private packageService: PackageService,
        @Inject(WINDOW) private window: Window,
        private notificationService: NotificationService,
        private translateService: TranslateService,
        private languageService: LanguageService,
        private searchStore: SearchStoreService
    ) {}

    private isSparePartsError(
        spareParts: SparePartGroupList | SparePartsResponseError
    ): spareParts is SparePartsResponseError {
        return (spareParts as SparePartsResponseError).error !== undefined;
    }

    private getNameWithAddedHints(part: SparePart) {
        const partOf = part.isPartOf;

        let hints = '';
        if (partOf && partOf.length > 0) {
            hints = partOf.reduce(
                (previousHints, isPartOf) =>
                    `${previousHints}\n${this.translateService.instant('PRODUCT.HINT.IS_PART_OF', {
                        isPartOf,
                    })}`,
                hints
            );
        }

        return `${part.name}${hints}`;
    }
}
